import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Generic recursive tree view component.
 *
 * Renders a tree of nodes with expand/collapse, selection,
 * optional drag-drop zones, and optional context menu trigger.
 * Device-agnostic — consumers provide node data and capability objects.
 */
import { useCallback, useState, useRef, useEffect } from 'react';
import { ChevronIcon, FolderIcon, DeleteIcon, NewFolderIcon } from './TreeIcons';
function TreeNodeRow({ node, depth, isExpanded, isSelected, onToggleExpand, selection, edit, contextMenu, drag, render, indentPx, emptyDirectoryMessage, expandedIds, selectedId, selectedIds, }) {
    const [isDragOver, setIsDragOver] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState('');
    const [isRenaming, setIsRenaming] = useState(false);
    const inputRef = useRef(null);
    const isDirectory = node.type === 'directory';
    const hasChildren = !!node.children?.length;
    // Both directories and non-directories can be dragged
    const isDraggable = drag?.draggable ?? false;
    const paddingLeft = depth * indentPx + 8;
    const canRename = edit?.enableInlineRename && edit.onRename;
    // Focus input when entering edit mode
    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);
    const handleClick = useCallback((e) => {
        e.stopPropagation();
        if (isEditing)
            return;
        // Multi-select with Ctrl/Cmd or Shift (non-directory items only)
        if (!isDirectory && selection?.onMultiSelect && (e.ctrlKey || e.metaKey || e.shiftKey)) {
            selection.onMultiSelect(node, { ctrlKey: e.ctrlKey || e.metaKey, shiftKey: e.shiftKey });
            return;
        }
        if (isDirectory) {
            onToggleExpand(node.id);
        }
        else if (hasChildren) {
            onToggleExpand(node.id);
            selection?.onSelect(node);
        }
        else {
            selection?.onSelect(node);
        }
    }, [node, isDirectory, onToggleExpand, selection, isEditing]);
    const handleDoubleClick = useCallback((e) => {
        e.stopPropagation();
        e.preventDefault();
        if (!canRename)
            return;
        setEditValue(node.name);
        setIsEditing(true);
    }, [node.name, canRename]);
    const handleRenameSubmit = useCallback(async () => {
        const trimmedValue = editValue.trim();
        if (!trimmedValue || trimmedValue === node.name || !edit?.onRename) {
            setIsEditing(false);
            return;
        }
        setIsRenaming(true);
        try {
            await edit.onRename(node, trimmedValue);
            setIsEditing(false);
        }
        catch (err) {
            // Keep editing mode open on error so user can try again
            console.error('[TreeView] Rename failed:', err);
        }
        finally {
            setIsRenaming(false);
        }
    }, [editValue, node, edit]);
    const handleRenameKeyDown = useCallback((e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleRenameSubmit();
        }
        else if (e.key === 'Escape') {
            e.preventDefault();
            setIsEditing(false);
        }
    }, [handleRenameSubmit]);
    const handleRenameBlur = useCallback(() => {
        // Submit on blur (unless already submitting)
        if (!isRenaming) {
            handleRenameSubmit();
        }
    }, [isRenaming, handleRenameSubmit]);
    const handleContextMenu = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        contextMenu?.onContextMenu(e, node);
    }, [node, contextMenu]);
    const handleDragStart = useCallback((e) => {
        if (!isDraggable) {
            e.preventDefault();
            return;
        }
        drag?.onDragStart(node, e);
    }, [node, isDraggable, drag]);
    const handleDragOverEvent = useCallback((e) => {
        if (!isDirectory)
            return;
        const allowed = drag?.onTreeDragOver ? drag.onTreeDragOver(node, e) : true;
        if (allowed) {
            e.preventDefault();
            e.stopPropagation();
            // Use 'copy' if the source only allows copy (e.g., disk browser items)
            e.dataTransfer.dropEffect =
                e.dataTransfer.effectAllowed === 'copy' ? 'copy' : 'move';
        }
    }, [node, isDirectory, drag]);
    const handleDragEnter = useCallback((e) => {
        if (!isDirectory)
            return;
        // Only light up the row if the consumer's onTreeDragOver predicate
        // says it would accept the drop. Without this guard, dragenter
        // ALWAYS toggles the highlight regardless of MIME-type acceptance
        // — so a tone dragged over a patches-section folder still glows
        // even after onTreeDragOver correctly rejects the drop. Consumers
        // without an onTreeDragOver fall back to legacy "highlight any
        // dragenter" behavior so non-Roland editors aren't affected.
        const accepted = drag?.onTreeDragOver ? drag.onTreeDragOver(node, e) : true;
        if (!accepted)
            return;
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    }, [node, isDirectory, drag]);
    const handleDragLeave = useCallback((e) => {
        if (!isDirectory)
            return;
        const rect = e.currentTarget.getBoundingClientRect();
        const { clientX: x, clientY: y } = e;
        if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
            setIsDragOver(false);
        }
    }, [isDirectory]);
    const handleDrop = useCallback((e) => {
        if (!isDirectory)
            return;
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
        drag?.onTreeDrop(node, e);
    }, [node, isDirectory, drag]);
    const handleKeyDown = useCallback((e) => {
        if (isEditing)
            return; // Don't handle while editing
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (isDirectory) {
                onToggleExpand(node.id);
            }
            else {
                selection?.onSelect(node);
            }
        }
    }, [node, isDirectory, onToggleExpand, selection, isEditing]);
    const icon = render?.renderIcon
        ? render.renderIcon(node, isExpanded)
        : isDirectory
            ? _jsx(FolderIcon, { isOpen: isExpanded })
            : null;
    const stateClass = isSelected
        ? 'ac-tree-node--selected'
        : isDragOver
            ? 'ac-tree-node--drag-over'
            : '';
    // Generate data-testid for e2e testing
    // Pattern: library-{type}-{slugified-id} (e.g., library-tone-my-tone, library-patch-bass-patch)
    const testId = node.type !== 'directory'
        ? `library-${node.type}-${node.id.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}`
        : undefined;
    return (_jsxs("div", { children: [_jsxs("div", { className: `ac-tree-node ${stateClass}${isEditing ? ' ac-tree-node--editing' : ''}${isDraggable && !isEditing ? ' ac-tree-node--draggable' : ''}`, style: { paddingLeft }, "data-testid": testId, "data-kind": isDirectory ? 'folder' : 'item', onClick: handleClick, onDoubleClick: canRename ? handleDoubleClick : undefined, onContextMenu: contextMenu ? handleContextMenu : undefined, onKeyDown: handleKeyDown, draggable: isDraggable && !isEditing, onDragStart: isDraggable && !isEditing ? handleDragStart : undefined, onDragOver: isDirectory ? handleDragOverEvent : undefined, onDragEnter: isDirectory ? handleDragEnter : undefined, onDragLeave: isDirectory ? handleDragLeave : undefined, onDrop: isDirectory ? handleDrop : undefined, role: "treeitem", tabIndex: isEditing ? -1 : 0, "aria-expanded": (isDirectory || hasChildren) ? isExpanded : undefined, children: [(isDirectory || hasChildren) && (_jsx("span", { className: "ac-tree-disclosure-btn", onClick: (e) => { e.stopPropagation(); onToggleExpand(node.id); }, children: _jsx(ChevronIcon, { isExpanded: isExpanded }) })), icon, isEditing ? (_jsx("input", { ref: inputRef, type: "text", className: "ac-tree-rename-input", value: editValue, onChange: (e) => setEditValue(e.target.value), onKeyDown: handleRenameKeyDown, onBlur: handleRenameBlur, disabled: isRenaming, onClick: (e) => e.stopPropagation() })) : (_jsx("span", { className: "ac-tree-node-name", children: node.name })), render?.renderTrailing?.(node), isDirectory && edit?.onCreateFolder && (_jsx("button", { className: "ac-tree-add-btn", onClick: (e) => { e.stopPropagation(); edit.onCreateFolder?.(node); }, title: `New folder in ${node.name}`, children: _jsx(NewFolderIcon, {}) })), edit?.onDelete && (_jsx("button", { className: "ac-tree-delete-btn", onClick: (e) => { e.stopPropagation(); edit.onDelete(node); }, title: `Delete ${node.name}`, children: _jsx(DeleteIcon, {}) }))] }), (isDirectory || hasChildren) && node.children && node.children.length > 0 && (_jsx("div", { className: "ac-collapse", "data-expanded": isExpanded, children: _jsx("div", { children: _jsx("div", { className: "ac-tree-children", children: node.children.map((child) => (_jsx(TreeNodeRow, { node: child, depth: depth + 1, isExpanded: expandedIds.has(child.id), isSelected: selectedIds ? selectedIds.has(child.id) : selectedId === child.id, onToggleExpand: onToggleExpand, selection: selection, edit: edit, contextMenu: contextMenu, drag: drag, render: render, indentPx: indentPx, emptyDirectoryMessage: emptyDirectoryMessage, expandedIds: expandedIds, selectedId: selectedId, selectedIds: selectedIds }, child.id))) }) }) })), isDirectory && isExpanded && (!node.children || node.children.length === 0) && (_jsx("div", { className: "ac-tree-empty", style: { paddingLeft: paddingLeft + 20 }, children: emptyDirectoryMessage }))] }));
}
// =========================================================================
// TreeView — public component
// =========================================================================
export function TreeView({ nodes, expandedIds: controlledExpandedIds, onToggleExpand: controlledOnToggle, selectedId, selectedIds, selection, edit, contextMenu, drag, render, indentPx = 16, emptyDirectoryMessage = 'Empty folder', }) {
    // Uncontrolled expand state fallback
    const [internalExpandedIds, setInternalExpandedIds] = useState(new Set());
    const expandedIds = controlledExpandedIds ?? internalExpandedIds;
    const onToggleExpand = controlledOnToggle ?? ((nodeId) => {
        setInternalExpandedIds((prev) => {
            const next = new Set(prev);
            if (next.has(nodeId)) {
                next.delete(nodeId);
            }
            else {
                next.add(nodeId);
            }
            return next;
        });
    });
    if (nodes.length === 0) {
        return _jsx("div", { className: "ac-tree-empty", style: { padding: '16px', textAlign: 'center' }, children: "No items" });
    }
    return (_jsx("div", { role: "tree", className: "ac-tree-view", children: nodes.map((node) => (_jsx(TreeNodeRow, { node: node, depth: 0, isExpanded: expandedIds.has(node.id), isSelected: selectedIds ? selectedIds.has(node.id) : selectedId === node.id, onToggleExpand: onToggleExpand, selection: selection, edit: edit, contextMenu: contextMenu, drag: drag, render: render, indentPx: indentPx, emptyDirectoryMessage: emptyDirectoryMessage, expandedIds: expandedIds, selectedId: selectedId, selectedIds: selectedIds }, node.id))) }));
}
//# sourceMappingURL=TreeView.js.map